import { useEffect, useState, useCallback, useRef } from "react";
import api from "../api";
import { useNavigate, useParams } from "react-router-dom";
import { Bounce, toast } from "react-toastify";
import Table from "../Table/Table";
import PopUp from "../PopUp";
import { buildFilterQueryParams } from "../utils/queryHelpers";
import useDebouncedValue from "../hooks/useDebouncedValue";

const ModuliProtocollati = () => {
  const { codiceFiscale } = useParams();
  const [moduli, setModuli] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [showPopUp, setShowPopUp] = useState(false);
  const [selectedModuliId, setSelectedModuliId] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [userRole, setUserRole] = useState(null);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnFilterFns, setColumnFilterFns] = useState({});
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const debouncedSorting = useDebouncedValue(sorting, 400);
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 400);
  const downloadRef = useRef(null);

  useEffect(() => {
    const role = localStorage.getItem("userRole");
    setUserRole(role);
    
    if (role !== "TRAINER" && role !== "ADMIN") {
      alert("Accedi come docente o amministratore per visualizzare i moduli protocollati!");
      navigate("/login");
      localStorage.clear();
    }
  }, [navigate]);

  const fetchModuli = useCallback(
    async ({
      roleParam,
      pageParam = page,
      sizeParam = pageSize,
      sortingParam = debouncedSorting,
      filtersParam = debouncedColumnFilters,
      filterFnsParam = columnFilterFns,
    } = {}) => {
      const currentRole =
        roleParam || userRole || localStorage.getItem("userRole");
      if (!currentRole) return;

      setLoading(true);
      try {
        const sortStrings = (sortingParam ?? []).map(
          ({ id, desc }) => `${id},${desc ? "desc" : "asc"}`
        );

        const params = {};

        // Only include pagination and filter params if user is ADMIN (backend pagination/filtering)
        if (currentRole === "ADMIN") {
          params.page = pageParam;
          params.size = sizeParam;

          // Transform trainer field filters to use nested path (trainer.nome, trainer.cognome, etc.)
          const transformedFilters = (filtersParam ?? []).map((filter) => {
            // Trainer fields
            if (filter.id === "nomeTrainer") {
              return { ...filter, id: "trainer.nome" };
            }
            if (filter.id === "cognomeTrainer") {
              return { ...filter, id: "trainer.cognome" };
            }
            if (filter.id === "codiceFiscaleTrainer") {
              return { ...filter, id: "trainer.codiceFiscale" };
            }
            return filter;
          });

          // Transform filter functions to match the new field names
          const transformedFilterFns = { ...filterFnsParam };
          // Trainer filter functions
          if (filterFnsParam["nomeTrainer"]) {
            transformedFilterFns["trainer.nome"] = filterFnsParam["nomeTrainer"];
            delete transformedFilterFns["nomeTrainer"];
          }
          if (filterFnsParam["cognomeTrainer"]) {
            transformedFilterFns["trainer.cognome"] = filterFnsParam["cognomeTrainer"];
            delete transformedFilterFns["cognomeTrainer"];
          }
          if (filterFnsParam["codiceFiscaleTrainer"]) {
            transformedFilterFns["trainer.codiceFiscale"] = filterFnsParam["codiceFiscaleTrainer"];
            delete transformedFilterFns["codiceFiscaleTrainer"];
          }

          const filterParams = buildFilterQueryParams(
            transformedFilters,
            transformedFilterFns
          );

          Object.assign(params, filterParams);
        }
        // For TRAINER, no pagination or filter params - fetch all data for frontend handling

        if (sortStrings.length > 0) {
          params.sort = sortStrings.join(";");
        }

        let response;

        if (currentRole === "ADMIN") {
          response = await api.get("/moduli-protocollati", {
            params,
          });
        } else {
          response = await api.get("/moduli-protocollati/trainer", {
            params: {
              ...params,
              codiceFiscale,
            },
          });
        }

        const rawData = Array.isArray(response.data?.content)
          ? response.data.content
          : Array.isArray(response.data)
          ? response.data
          : [];
        const pageInfo = response.data?.page;

        const flattened = rawData.map((m) => ({
          ...m,
          nomeTrainer: m.trainer?.nome || "-",
          cognomeTrainer: m.trainer?.cognome || "-",
          codiceFiscaleTrainer: m.trainer?.codiceFiscale || "-",
        }));

        setModuli(flattened);
        // For ADMIN: use backend pagination info, for TRAINER: use total count of all data
        setTotalRows(
          currentRole === "ADMIN"
            ? pageInfo?.totalElements ?? flattened.length
            : flattened.length
        );
      } catch (error) {
        console.error(error);
        toast.error(error?.response?.data?.message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      } finally {
        setLoading(false);
      }
    },
    [
      userRole,
      codiceFiscale,
      page,
      pageSize,
      debouncedSorting,
      debouncedColumnFilters,
      columnFilterFns,
    ]
  );

  // For ADMIN: refetch when pagination/filters change
  useEffect(() => {
    if (!userRole || userRole !== "ADMIN") return;
    fetchModuli({
      roleParam: userRole,
    });
  }, [userRole, page, pageSize, debouncedSorting, debouncedColumnFilters, columnFilterFns, fetchModuli]);

  // For TRAINER: only fetch once (frontend handles pagination/filtering)
  useEffect(() => {
    if (!userRole || userRole !== "TRAINER") return;
    // TRAINER: fetch all data once, frontend handles the rest
    fetchModuli({
      roleParam: userRole,
      pageParam: 0,
      sizeParam: 10000, // Large size to get all data
    });
  }, [userRole, fetchModuli]);

  const handleDelete = async () => {
    if (!selectedModuliId) {
      return;
    }
    try {
      setLoading(true);

      const response = await api.delete(
        `/moduli-protocollati/${selectedModuliId}`
      );
      if (response.status === 204) {
        toast.success("Modulo eliminato con successo!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        await fetchModuli({
          roleParam: userRole,
        });
      } else {
        toast.error("Qualcosa è andato storto!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
      setSelectedModuliId(null);
      setShowPopUp(false);
    }
  };

  const baseColumns = [
    { field: "nomeTrainer", header: "Nome" },
    { field: "cognomeTrainer", header: "Cognome" },
    { field: "codiceFiscaleTrainer", header: "Codice Fiscale" },
    { field: "titoloModulo", header: "Titolo Modulo" },
    { field: "oreModulo", header: "Ore" },
    { field: "modalitàErogazione", header: "Modalità" },
    { field: "materialeAttrezzature", header: "Materiale" },
    { field: "previsteDispense", header: "Dispense" },
    { field: "proveFinali", header: "Prove Finali" },
    { field: "descrizione", header: "Descrizione" },
  ];

  const actionsColumn = {
    header: "Azioni",
    accessorKey: "azioni",
    Cell: ({ row }) => (
      <button
        className="outside-delete-btn"
        onClick={(e) => {
          e.stopPropagation();
          setSelectedModuliId(row.original.idModuloTrainer);
          setShowPopUp(true);
        }}
      >
        <i className="fa-solid fa-trash"></i> Elimina
      </button>
    ),
  };

  // Only include actions column for TRAINER role
  const columns =
    userRole === "TRAINER" ? [...baseColumns, actionsColumn] : baseColumns;

  const handleRowClick = (modulo) => {
    const trainerCode = modulo.codiceFiscaleTrainer || codiceFiscale;
    if (!trainerCode) {
      toast.error("Codice fiscale docente non disponibile per questo modulo.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
      return;
    }

    navigate(
      `/moduli-protocollati/${modulo.idModuloTrainer}?codiceFiscale=${encodeURIComponent(
        trainerCode
      )}`
    );
  };

  const handlePageChange = (pageNumber) => {
    setPage(Math.max(pageNumber - 1, 0));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const handleSortingChange = (newSorting) => {
    setSorting(newSorting);
    setPage(0);
  };

  const handleColumnFiltersChange = (newFilters) => {
    setColumnFilters(newFilters);
    setPage(0);
  };

  const handleColumnFilterFnsChange = (newFilterFns) => {
    setColumnFilterFns(newFilterFns);
    setPage(0);
  };

  const handleExport = async (format) => {
    setIsDownloadOpen(false);
    setLoading(true);
    try {
      const response = await api.get("/export/protocolli-modulo", {
        params: { format },
        responseType: "blob",
      });
      
      if (response.status === 204) {
        toast.info("Non ci sono dati da scaricare", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        return;
      }
      
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const contentDisposition = response.headers["content-disposition"];
      let filename = `protocolli-modulo.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Errore durante il download", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadRef.current && !downloadRef.current.contains(event.target)) {
        setIsDownloadOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="content-page">
      {loading && (
        <div className="overlay">
          <div className="spinner"></div>
        </div>
      )}

      <PopUp
        open={showPopUp}
        setOpen={setShowPopUp}
        header="Conferma Eliminazione"
        content="Sei sicuro di voler rimuovere questo moduli protocollati?"
        deleteFunction={handleDelete}
      />

      <div className="page-header">
        <div className="page-title">Moduli Protocollati</div>
        <div className="controls">
          <div className="download-menu" ref={downloadRef}>
            <button
              className="download-btn"
              onClick={() => setIsDownloadOpen((prev) => !prev)}
            >
              Scarica
            </button>
            {isDownloadOpen && (
              <div className="download-dropdown">
                <button onClick={() => handleExport("csv")}>CSV</button>
                <button onClick={() => handleExport("xlsx")}>Excel</button>
              </div>
            )}
          </div>
          {userRole === "TRAINER" && (
            <div className="add-btn">
              <button onClick={() => navigate("/aggiungi-moduli-protocollati")}>
                + Nuovo modulo
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="page-content">
          <Table
            columns={columns}
            data={moduli}
            onRowClick={handleRowClick}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            {...(userRole === "ADMIN" ? {
              // Backend pagination and filtering for ADMIN
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange,
              totalRows: totalRows,
              perPage: pageSize,
              currentPage: page + 1,
              columnFilters,
              onColumnFiltersChange: handleColumnFiltersChange,
              columnFilterFns,
              onColumnFilterFnsChange: handleColumnFilterFnsChange,
            } : {
              // Frontend pagination and filtering for TRAINER
              // No pagination/filter props passed - Table handles internally
            })}
          />
        
      </div>
    </div>
  );
};

export default ModuliProtocollati;
